import React from 'react';
import { Typography, Descriptions, Tag } from 'antd';
import { MoreOutlined, IdcardOutlined, ExclamationCircleOutlined, ClockCircleOutlined, CalendarOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface OtherTabProps {
  asset: any;
  safeSchedules: any[];
  maintenanceTypes: any[];
  maintenanceHistory?: any[]; // Thêm prop cho lịch sử bảo dưỡng
}

const OtherTab: React.FC<OtherTabProps> = ({ asset, safeSchedules, maintenanceTypes, maintenanceHistory = [] }) => {
  // Kiểm tra xem có lịch trình nào sắp tới hạn không (30 ngày)
  const isMaintenanceDue = (nextDueDate: string) => {
    const nextDate = new Date(nextDueDate);
    const today = new Date();
    const diffTime = nextDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays <= 30 && diffDays > 0;
  };

  // Kiểm tra xem có lịch trình nào quá hạn không
  const isMaintenanceOverdue = (nextDueDate: string) => {
    const nextDate = new Date(nextDueDate);
    const today = new Date();
    const diffTime = nextDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays < 0;
  };

  // Tính số ngày còn lại hoặc quá hạn
  const getDaysStatus = (nextDueDate: string) => {
    const nextDate = new Date(nextDueDate);
    const today = new Date();
    const diffTime = nextDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return `Quá hạn ${Math.abs(diffDays)} ngày`;
    } else if (diffDays === 0) {
      return 'Đến hạn hôm nay';
    } else if (diffDays <= 30) {
      return `Còn ${diffDays} ngày`;
    }
    return null;
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={5} style={{ marginBottom: 16, textTransform: 'uppercase' }}>
        <MoreOutlined style={{ marginRight: 8 }} />
        Thông tin khác
      </Title>
      
      <Descriptions column={2} size="small" style={{ marginBottom: 16 }}>
        <Descriptions.Item label={
          <span>
            <IdcardOutlined style={{ marginRight: 4, color: '#1890ff' }} />
            ID tài sản
          </span>
        }>
          <Tag color="blue" style={{ fontWeight: 'bold' }}>{asset?.id || '—'}</Tag>
        </Descriptions.Item>
        
        <Descriptions.Item label="Chu kỳ kiểm kê">
          {asset?.audit_cycle_months ? `${asset.audit_cycle_months} tháng` : '—'}
        </Descriptions.Item>
        
        <Descriptions.Item label="Thời hạn bảo hành">
          {asset?.warranty_period_months ? `${asset.warranty_period_months} tháng` : '—'}
        </Descriptions.Item>
        
        <Descriptions.Item label="Ngày tạo">
          {asset?.created_at ? new Date(asset.created_at).toLocaleDateString('vi-VN') : '—'}
        </Descriptions.Item>
        
        <Descriptions.Item label="Ngày cập nhật">
          {asset?.updated_at ? new Date(asset.updated_at).toLocaleDateString('vi-VN') : '—'}
        </Descriptions.Item>
      </Descriptions>
      
      {safeSchedules.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Title level={5} style={{ marginBottom: 16, textTransform: 'uppercase' }}>
            Loại bảo dưỡng & chu kỳ:
          </Title>
          <div style={{ margin: '8px 0 0 0', padding: 0 }}>
            {safeSchedules.map((ms: any, index: number) => {
              const mt = maintenanceTypes.find((t: any) => t.id === ms.maintenance_type_id);
              const daysStatus = ms.next_due_date ? getDaysStatus(ms.next_due_date) : null;
              const isDue = ms.next_due_date ? isMaintenanceDue(ms.next_due_date) : false;
              const isOverdue = ms.next_due_date ? isMaintenanceOverdue(ms.next_due_date) : false;
              
              return (
                <div key={ms.id || ms.maintenance_type_id || index} style={{ 
                  marginBottom: 12, 
                  padding: 12, 
                  border: '1px solid #f0f0f0', 
                  borderRadius: 8,
                  backgroundColor: isOverdue ? '#fff2f0' : isDue ? '#fff7e6' : '#fafafa'
                }}>
                  <Typography.Paragraph style={{ marginBottom: 8, fontSize: 14 }}>
                    <Typography.Text strong style={{ fontSize: 15 }}>
                      {mt ? mt.name.toUpperCase() : `LOẠI ID: ${ms.maintenance_type_id}`}
                    </Typography.Text>
                    {ms.cycle_months && (
                      <span style={{ marginLeft: 8, color: '#888' }}>
                        - Chu kỳ: <Typography.Text code>{ms.cycle_months} tháng</Typography.Text>
                      </span>
                    )}
                  </Typography.Paragraph>
                  
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 13 }}>
                    {ms.last_maintenance_date && (
                      <div style={{ color: '#52c41a' }}>
                        <CalendarOutlined style={{ marginRight: 4 }} />
                        Lần cuối: {new Date(ms.last_maintenance_date).toLocaleDateString('vi-VN')}
                      </div>
                    )}
                    
                    {ms.next_due_date && (
                      <div style={{ color: '#1890ff' }}>
                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                        Tiếp theo: {new Date(ms.next_due_date).toLocaleDateString('vi-VN')}
                      </div>
                    )}
                    
                    {daysStatus && (
                      <div style={{ 
                        color: isOverdue ? '#ff4d4f' : isDue ? '#faad14' : '#52c41a',
                        fontWeight: 'bold'
                      }}>
                        <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                        {daysStatus}
                        {isOverdue && (
                          <Tag color="red" style={{ marginLeft: 8, fontSize: 11 }}>QUÁ HẠN</Tag>
                        )}
                        {isDue && !isOverdue && (
                          <Tag color="orange" style={{ marginLeft: 8, fontSize: 11 }}>SẮP TỚI HẠN</Tag>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default OtherTab; 